import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { LOG_POLL_INTERVAL_MS, useLogs } from '../../api/hooks';
import { SOURCE_LABELS } from '../../api/types';
import type { LogLine } from '../../api/types';
import { Card, CardHeader } from '../../components/Card';
import { useFormatters } from '../../time/format';

/** How far back the console asks for, and what the options mean to someone reading them. */
const WINDOWS: { minutes: number; label: string }[] = [
  { minutes: 5, label: '5m' },
  { minutes: 30, label: '30m' },
  { minutes: 120, label: '2h' },
  { minutes: 720, label: '12h' },
];

/**
 * Component names as the API exposes them, in pipeline order with a description each.
 *
 * Ordered by where a thing happens rather than alphabetically, because the reason to switch
 * component is to follow an alert forwards: it arrived (webhook or poller), it became a case
 * (correlate), it was triaged (plan through gate). A component the deployment does not have
 * is filtered out against the server's list rather than hidden here.
 */
const COMPONENTS: { name: string; hint: string }[] = [
  { name: 'poller', hint: 'Scheduled pulls from each vendor API' },
  { name: 'webhook', hint: 'Vendor-pushed deliveries' },
  { name: 'correlate', hint: 'Alerts joined into cases' },
  { name: 'plan', hint: 'Collection planning' },
  { name: 'pack', hint: 'Evidence assembly' },
  { name: 'analyse', hint: 'Analyst model' },
  { name: 'gate', hint: 'Policy decisions' },
  { name: 'execute', hint: 'Action execution' },
  { name: 'record', hint: 'Case and decision writes' },
  { name: 'expire', hint: 'Approval expiry sweep' },
  { name: 'api', hint: 'This dashboard' },
];

interface LogConsoleProps {
  enabled: boolean;
  /** Pollable sources, for the filter. Empty means every source. */
  sources: string[];
  source: string;
  onSourceChange: (source: string) => void;
}

/**
 * A live tail of the platform's own CloudWatch logs.
 *
 * Here so that diagnosing a stalled intake does not require an AWS console login. The people
 * who need to answer "why has Defender gone quiet" are not necessarily the people with an
 * account in this org, and the alternative in practice is that nobody looks until an alarm
 * fires — by which time the window that would have explained it has rolled out of the
 * retention the poller's log group is configured for.
 *
 * Read-only by construction: the route behind it is a GET holding a `FilterLogEvents` grant
 * on named log groups, so there is nothing here that can be turned into a write.
 */
export function LogConsole({ enabled, sources, source, onSourceChange }: LogConsoleProps) {
  const [component, setComponent] = useState('poller');
  const [minutes, setMinutes] = useState(30);
  const [live, setLive] = useState(true);
  const { time } = useFormatters();

  const logs = useLogs(enabled, { component, source, minutes, live });
  const available = logs.data?.components;

  // Filtering by source only means anything for components that log one. The orchestration
  // stages work on a case, whose alerts may come from several sources, so a source filter
  // there would return nothing and read as "no activity" rather than "wrong question".
  const filterable = component === 'poller' || component === 'webhook';

  return (
    <Card className="p-0">
      <div className="flex flex-wrap items-start justify-between gap-3 p-5 pb-3">
        <CardHeader
          title="Live logs"
          subtitle={logs.data?.log_group ?? 'This platform’s own CloudWatch output'}
        />
        <LiveToggle live={live} onToggle={() => setLive((was) => !was)} fetching={logs.isFetching} />
      </div>

      <div className="flex flex-wrap gap-4 px-5 pb-3">
        <Filter label="Component">
          <select
            value={component}
            onChange={(event) => setComponent(event.target.value)}
            className="rounded-lg border border-line bg-sunken px-2 py-1 text-xs text-body"
          >
            {COMPONENTS.filter(({ name }) => !available || available.includes(name)).map(
              ({ name, hint }) => (
                <option key={name} value={name} title={hint}>
                  {name} — {hint}
                </option>
              ),
            )}
          </select>
        </Filter>

        {filterable ? (
          <Filter label="Source">
            <select
              value={source}
              onChange={(event) => onSourceChange(event.target.value)}
              className="rounded-lg border border-line bg-sunken px-2 py-1 text-xs text-body"
            >
              <option value="">All sources</option>
              {sources.map((name) => (
                <option key={name} value={name}>
                  {SOURCE_LABELS[name as keyof typeof SOURCE_LABELS] ?? name}
                </option>
              ))}
            </select>
          </Filter>
        ) : null}

        <Filter label="Window">
          <div className="flex gap-1">
            {WINDOWS.map(({ minutes: value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => setMinutes(value)}
                className={`rounded-lg px-2 py-1 text-xs font-medium ${
                  minutes === value
                    ? 'bg-brand text-on-brand'
                    : 'border border-line bg-sunken text-muted hover:text-body'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </Filter>
      </div>

      <Terminal
        lines={logs.data?.events ?? []}
        pending={logs.isPending}
        error={logs.error?.message ?? ''}
        live={live}
        formatTime={time}
      />

      <p className="px-5 py-2 text-[11px] text-subtle">
        {logs.data?.truncated
          ? `More than ${logs.data.events.length} lines in the last ${minutes} minutes — showing the most recent. Narrow the window to see the rest.`
          : `${logs.data?.events.length ?? 0} lines in the last ${minutes} minutes.`}
      </p>
    </Card>
  );
}

function Filter({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-subtle">
        {label}
      </span>
      {children}
    </label>
  );
}

function LiveToggle({
  live,
  onToggle,
  fetching,
}: {
  live: boolean;
  onToggle: () => void;
  fetching: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${
        live
          ? 'border-success-border bg-success-bg text-success-fg'
          : 'border-line bg-sunken text-muted hover:text-body'
      }`}
      aria-pressed={live}
    >
      <span
        className={`h-2 w-2 rounded-full ${
          live ? `bg-success-fg ${fetching ? 'animate-pulse' : ''}` : 'bg-line-strong'
        }`}
      />
      {live ? `Live — polling every ${LOG_POLL_INTERVAL_MS / 1000}s` : 'Paused'}
    </button>
  );
}

interface TerminalProps {
  lines: LogLine[];
  pending: boolean;
  error: string;
  live: boolean;
  formatTime: (value: string) => string;
}

function Terminal({ lines, pending, error, live, formatTime }: TerminalProps) {
  const viewport = useRef<HTMLDivElement>(null);
  const [pinned, setPinned] = useState(true);

  // Whether the reader is at the bottom is decided before the new lines paint, because
  // appending to the list is itself what moves `scrollTop` away from the end. Measuring after
  // the fact reports "not at the bottom" for anyone who was, and auto-scroll then switches
  // itself off the first time a line arrives.
  useLayoutEffect(() => {
    const element = viewport.current;
    if (!element) return;
    const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
    setPinned(distance < 40);
  }, [lines]);

  useEffect(() => {
    const element = viewport.current;
    // Scrolled up on purpose means reading something, and yanking the view back to the bottom
    // five seconds later loses it. Auto-scroll resumes as soon as the reader returns there.
    if (element && pinned) element.scrollTop = element.scrollHeight;
  }, [lines, pinned]);

  return (
    <div className="relative">
      <div
        ref={viewport}
        // `role="log"` with a polite live region, so a screen reader announces new lines
        // instead of the whole window every five seconds.
        role="log"
        aria-live="polite"
        aria-label="Platform log output"
        className="scroll-thin h-[22rem] overflow-y-auto border-y border-console-line bg-console-bg px-4 py-3 font-mono text-[11px] leading-relaxed"
      >
        {pending ? (
          <p className="text-console-dim">Reading log group…</p>
        ) : error ? (
          <p className="text-console-error">{error}</p>
        ) : lines.length === 0 ? (
          <p className="text-console-dim">
            Nothing logged in this window. For a component that has never been invoked there is
            no log group at all, which reads the same from here.
          </p>
        ) : (
          lines.map((line, index) => (
            <Line
              // Timestamp and index rather than the message: identical lines are normal in a
              // log and a message key would make React drop the duplicates.
              key={`${line.timestamp}-${index}`}
              line={line}
              formatTime={formatTime}
            />
          ))
        )}
      </div>

      {!pinned && live ? (
        <button
          type="button"
          onClick={() => {
            const element = viewport.current;
            if (element) element.scrollTop = element.scrollHeight;
            setPinned(true);
          }}
          className="absolute bottom-3 right-4 rounded-full border border-console-line bg-console-bg/90 px-3 py-1 text-[11px] font-medium text-console-text"
        >
          Jump to latest
        </button>
      ) : null}
    </div>
  );
}

/**
 * Levels, matched against the text rather than parsed out of it.
 *
 * Lines arrive from two writers with different shapes: `log_event` emits JSON, and the Lambda
 * runtime emits `START`/`END`/`REPORT` around every invocation. Colouring by substring covers
 * both without needing to know which is which, and the failure mode of a miss is a line
 * rendered in the default colour.
 */
function toneOf(message: string): string {
  if (/\b(ERROR|CRITICAL|Task timed out|Traceback)\b/.test(message)) return 'text-console-error';
  if (/\b(WARN|WARNING)\b/.test(message)) return 'text-console-warn';
  if (/^(START|END|REPORT|INIT_START)\b/.test(message)) return 'text-console-dim';
  if (/_failed|_exhausted|"ok":\s*false/.test(message)) return 'text-console-warn';
  if (/_complete|"ok":\s*true/.test(message)) return 'text-console-ok';
  return 'text-console-text';
}

function Line({ line, formatTime }: { line: LogLine; formatTime: (value: string) => string }) {
  return (
    <div className="flex gap-3 whitespace-pre-wrap break-all">
      <span className="shrink-0 tabular-nums text-console-time">{formatTime(line.timestamp)}</span>
      <span className={toneOf(line.message)}>{line.message}</span>
    </div>
  );
}
